import { useState, useEffect } from 'react';
import { useAuth } from '@/hooks/use-auth';
import type { MarsPriceTier } from '@/lib/mars-prices';

const PROFIT_SETTINGS_KEY = 'profit-calculation-settings';

export interface ProfitSettings {
  marsPriceTier: MarsPriceTier;
  enabled: boolean; // Whether to use Mars prices for profit calculation
}

const DEFAULT_SETTINGS: ProfitSettings = {
  marsPriceTier: 'pesin',
  enabled: false, // Default to false to maintain backward compatibility
};

export function useProfitSettings() {
  const { companyId } = useAuth();
  const [settings, setSettings] = useState<ProfitSettings>(DEFAULT_SETTINGS);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!companyId) {
      setLoading(false);
      return;
    }

    try {
      const saved = localStorage.getItem(`${PROFIT_SETTINGS_KEY}-${companyId}`);
      if (saved) {
        const parsed = JSON.parse(saved);
        setSettings({ ...DEFAULT_SETTINGS, ...parsed });
      } else {
        setSettings(DEFAULT_SETTINGS);
      }
    } catch (error) {
      console.error('Error loading profit settings:', error);
      setSettings(DEFAULT_SETTINGS);
    } finally {
      setLoading(false);
    }
  }, [companyId]);

  const updateSettings = (newSettings: Partial<ProfitSettings>) => {
    if (!companyId) return;

    const updated = { ...settings, ...newSettings };
    try {
      localStorage.setItem(`${PROFIT_SETTINGS_KEY}-${companyId}`, JSON.stringify(updated));
      setSettings(updated);
    } catch (error) {
      console.error('Error saving profit settings:', error);
    }
  };

  return {
    settings,
    updateSettings,
    loading,
  };
}
